import React from 'react';
import { Alert, Flex, Modal, Spin, Typography } from '@duxton-web/ui';

import { formatSqlForDisplay, getMetricQueryErrorMessage } from '../../utils';
import { useGetMetricQuery } from '@/hooks/usePlannerQueries';

interface Props {
  experimentName: string;
  metricId: string;
  metricDisplayName: string;
  open: boolean;
  onClose: () => void;
}

const SqlPreviewModal: React.FC<Props> = ({
  experimentName,
  metricId,
  metricDisplayName,
  open,
  onClose,
}) => {
  const { data, isLoading, isError, error } = useGetMetricQuery(
    experimentName,
    metricId,
    'overall',
    {
      enabled: open,
    },
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      title={`Formatted SQL — ${metricDisplayName}`}
    >
      {isLoading && (
        <Flex align="center" justify="center" gap={8} style={{ padding: 32 }}>
          <Spin size="small" />
          <Typography.Text type="secondary">
            Formatting the latest successful SQL…
          </Typography.Text>
        </Flex>
      )}

      {isError && (
        <Alert
          type="error"
          showIcon
          message={getMetricQueryErrorMessage(error)}
        />
      )}

      {!isLoading && !isError && data && (
        <Flex vertical gap={16}>
          <Alert
            type="info"
            showIcon
            message={`Showing the latest successful SQL for run date ${data.runDate}.`}
            description="Preview uses the JSON query endpoint and formats the SQL client-side for readability."
          />

          {(data.queries ?? []).map((query, index) => (
            <div key={`${query.metrics.join('-')}-${index}`}>
              <Typography.Text strong>Query {index + 1}</Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ marginBottom: 8 }}
              >
                Metrics: {query.metrics.join(', ')}
              </Typography.Paragraph>
              <pre
                style={{
                  margin: 0,
                  padding: 16,
                  maxHeight: 420,
                  overflow: 'auto',
                  borderRadius: 8,
                  border: '1px solid #f0f0f0',
                  background: '#141414',
                  color: '#f5f5f5',
                  fontSize: 12,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {formatSqlForDisplay(query.sql)}
              </pre>
            </div>
          ))}
        </Flex>
      )}
    </Modal>
  );
};

export default SqlPreviewModal;
